import { FleetControl } from '@vitesse/proto/vitesse/v1/fleet_control_pb.js';
import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { ShellIdentity } from '../../core/shell-identity';
import { AI_URL } from '../../core/env';
import type { BackendClient } from '../bridge/backend-client';
import { ControllerManager } from '../managers/controller-manager';
import { CameraManager } from '../managers/camera-manager';
import { CameraStreamManager } from '../components/camera/camera-stream-manager';
import { ModelManager } from '../managers/model-manager';
import { IntegrationManager } from '../managers/integration-manager';
import { DashboardManager } from '../managers/dashboard-manager';
import { sessionGrant } from '../../dashboards/runtime/capability';
import { TransportManager } from '../managers/transport-manager';
import { PluginTypesManager } from '../managers/plugin-types-manager';
import { ConnectionManager } from '../managers/connection-manager';
import { FleetManager } from '../managers/fleet-manager';
import { FindingsManager } from '../managers/findings-manager';
import { findingNames } from '../components/findings/finding-labels';
import { AiAdmin } from '@vitesse/proto/vitesse/ai/v1/ai_admin_pb.js';
import { NotificationManager } from '../managers/notification-manager';
import { AiManager, configureAiPlane, filesOf, implementDomPlatformTools, implementPlatformTool, treeRunsOf } from '@vitesse/base-ui/ai';
import { RunStatus } from '@vitesse/proto/vitesse/ai/v1/agent_pb.js';
import { registerModelTools } from '../ai-tools/model-tools';
import { registerModelWorkCard } from '../ai-tools/model-card';
import { registerReportCard } from '../ai-tools/report-card';
import { unknownPlace } from '../ai-tools/gateway-resolve';
import { registerModelSimulateTool } from '../ai-tools/model-simulate-tool';
import { registerSymbolTools } from '../ai-tools/symbol-tools';
import { allWidgets, registerDashboardTools } from '../ai-tools/dashboard-tools';
import { registerPlantMentions } from '../ai-tools/mention-providers';
import { registerProgramTools } from '../ai-tools/program-tools';
import { registerHistoryTools } from '../ai-tools/history-tools';
import { registerSandboxTools } from '../ai-tools/sandbox-tools';
import { registerIntegrationTools } from '../ai-tools/integration-tools';
import { registerGatewayTools } from '@vitesse/base-ui/ai';
import type { AiClient } from '@vitesse/base-ui/ai';
import { gatewayLabel, type GatewayEntry } from '../managers/gateway-entry';
import { entryConnState } from '../managers/conn-state';
import type { CloudServerLite, FleetGateway } from '../../core/bridge/connect-client';
import { lanCandidates } from '../../core/env';
import { getSession, identityFrom, orgNamesFrom, Perm } from '@vitesse/base-ui';
import { t } from '@vitesse/base-ui/i18n';
import { LocaleController } from '@vitesse/base-ui/i18n';
import { slideDirection, type NavNode, type SlideDir } from '@vitesse/base-ui/nav/nav-tree';
import { parseControllerOpen, parseRoute, routePath, type ControllerOpen, type Route } from './router';
import type { CatalogEntry } from '../../dashboards/catalog';
import '@vitesse/base-ui/elements/vitesse-header';
import './vitesse-home';
import './vitesse-tree';
import './vitesse-gateway-detail';
import './controller-detail';
import './vitesse-camera-detail';
import './vitesse-model-detail';
import './vitesse-integration-detail';
import './vitesse-dashboard-detail';
import './vitesse-canvas';
import './vitesse-findings';
import './vitesse-claim-dialog';
import '@vitesse/base-ui/ai'; // registers <vitesse-ai-panel>
import '@vitesse/base-ui/elements/vitesse-ai-rail'; // registers <vitesse-ai-rail>
import { AiBusyTally, RAIL_MAX_W } from '@vitesse/base-ui/elements/rail-resize';
import '@vitesse/base-ui/elements/vitesse-marketplace-panel'; // registers <vitesse-marketplace-panel>
import type { Listing as MarketplaceListing } from '@vitesse/base-ui/elements/vitesse-marketplace-panel';
import { officialWidgets, officialForSource } from '../../dashboards/marketplace-official';
import { catalogForSource } from '../../dashboards/catalog';
import './vitesse-widget-publish-dialog';
import type { PublishRequest } from './vitesse-widget-publish-dialog';
import type { DashboardTile } from '../../core/domain/dashboard';
import type { TilePublish } from './vitesse-dashboard-detail';

/** One published widget as the shelf lists it. */
interface OrgWidgetRow {
  widget: string;
  title: string;
  summary: string;
  source: string;
  publishedByOrg: string;
  publishedByOrgName: string;
  official: boolean;
}
import '../../dashboards/widget-demo';
import '../../dashboards/widget-preview';
import { checkWidgetSource } from '../../dashboards/widget-check';

/** What a note looks like on a board — the marketplace card's demo content. */
const NOTE_DEMO =
  '<h3>Shift handover</h3><p>Line 2 back at <strong>full rate</strong> after the <code>E-stop</code> reset.</p>' +
  '<ul data-type="taskList"><li data-checked="true"><label><input type="checkbox" checked></label><div>Check press temperature</div></li>' +
  '<li data-checked="false"><label><input type="checkbox"></label><div>Log the <mark>batch 41</mark> sample</div></li></ul>';
import '../../dashboards/widget-host';
import { DENY_ALL, type CapabilityGrant } from '../../dashboards/runtime/capability';
import './vitesse-widget-install-dialog';
import type { InstallWidget } from './vitesse-widget-install-dialog';
// The pane rule is a single product decision, so both shells run the same function rather than
// each re-deciding what opening the AI rail should do to the tree.
import { onToggleAi, onToggleTree } from '@vitesse/base-ui/elements/rail-resize';
import '@vitesse/base-ui/elements/vitesse-icon';
import { showResultError } from '../errors';
import { openOrgBilling } from '@vitesse/base-ui';
import '@vitesse/base-ui/elements/vitesse-gate-overlay';
// The app's own code viewer, lent to the assistant's artifact tabs — see artifactCode below. Monaco
// itself is still lazy: this registers the element, not the editor.
import './vitesse-code-view';

// The nav-event payload IS a route (router.ts owns the shape + URL grammar).
type View = Route['view'];
type NavDetail = Route;

/*
 * <vitesse-app> — app shell. LIGHT DOM (createRenderRoot → this) so the agent's
 * global SCSS styles the whole tree of ported components. Header over the agent
 * `.main-layout` row: left tree rail + main content. Navigation is event-driven.
 */
@customElement('vitesse-app')
export class VitesseApp extends LitElement {
  @property({ attribute: false }) backend?: BackendClient;
  // The AI plane's main-thread handle (its own worker). Set once at mount; the app-global
  // AiManager (per-user, like FleetManager) is created from it, and its handshake is DEFERRED to
  // the first time the AI rail opens (idle-until-used — don't mint a token the user never uses).
  @property({ attribute: false }) ai?: AiClient;
  @property({ attribute: false }) identity?: ShellIdentity;
  // The account widget lives in LIGHT DOM (its CSS lands in document.head, which can't
  // reach shadow DOM). main/index.ts hands us the node; we render it INSIDE
  // <vitesse-header> with slot="user" so the header's real shadow <slot name="user">
  // projects it into the right-aligned `.actions` (it stays in the light DOM, so
  // that CSS still reaches it). Putting it on <vitesse-app> (light DOM) instead
  // dropped it into document flow at top-left — the bug.
  @property({ attribute: false }) userEl?: HTMLElement;

  // The org switcher — same light-DOM + slot projection as userEl, for the same
  // reason (its CSS lives in document.head and can't reach shadow DOM).
  @property({ attribute: false }) orgEl?: HTMLElement;

  // The org's gateways: one entry per CLAIMED gateway (roster + its 3 live
  // per-gateway managers). home + tree render a card + tree node for EVERY one,
  // connected or not. Rebuilt when the fleet resolves/changes; the shell
  // subscribes to each manager's `change` (→ requestUpdate), so home/tree are
  // pure renderers of this array — no per-gateway listeners of their own.
  @state() private entries: GatewayEntry[] = [];
  /** The RAW fleet roster as the server scoped it (super-admin: every org's boxes;
   *  anyone else: their own org's) — feeds the dashboards map, which must show
   *  exactly what the fleet app shows. `entries` is the OWNED subset with managers;
   *  this is presentation-only, no managers. */
  @state() private rawFleet: FleetGateway[] = [];
  /** Our private cloud servers off the same stream — present only when the server
   *  decided this viewer may see them (Vitesse super-admins). */
  @state() private cloudServers: CloudServerLite[] = [];
  @state() private fleet?: FleetManager;
  // The app-global camera media plane (RTCPeerConnections live main-thread). One
  // per session; shared across every camera view (keyed by gateway+camera).
  @state() private cameraStreams?: CameraStreamManager;
  @state() private view: View = 'home';
  @state() private selectedControllerId = '';
  /** What the controller page was asked to open with on arrival, stamped — see `applyRoute`. */
  @state() private controllerOpen?: ControllerOpen & { seq: number };
  private openSeq = 0;
  @state() private selectedCameraId = '';
  @state() private selectedModelId = '';
  @state() private selectedIntegrationId = '';
  @state() private selectedDashboardId = '';
  @state() private treeOpen = true;
  @state() private aiOpen = false;
  /** The marketplace rail — opens beside (left of) the AI rail, and like the AI
   *  rail it collapses the tree: three panes plus content is a corridor. */
  @state() private marketOpen = false;
  /** A marketplace widget awaiting a dashboard to land on (the install dialog is
   *  open while set). Cleared on install or cancel. */
  @state() private installWidget?: InstallWidget;
  /** The assistant is working. The rail can be closed for room while it does — it collapses to zero
   *  width rather than unmounting — so the header carries the signal instead. */
  @state() private aiBusy = false;
  /** WHICH surfaces are working, so the answer is "any of them" rather than "the last one to
   *  speak". A stack draws three at once — see AiBusyTally. */
  private readonly busyTally = new AiBusyTally();
  /** It finished while the rail was closed. Cleared by opening it, because that is what "you have
   *  seen it" means here. */
  @state() private aiUnseen = false;
  @state() private claimOpen = false;
  // The app-global AI manager (per-user). Created from `ai`; the handshake fires on first rail-open.
  @state() private aiManager?: AiManager;
  // The org's findings log, read through the same AI worker — one list, shown by whichever view
  // is open: the Findings page or a controller's Findings tab.
  @state() private findings?: FindingsManager;
  /** Unregisters the DOM platform tools, so a torn-down shell does not leave them answering. */
  private aiToolsOff?: () => void;
  /** …and this app's own model tools, for the same reason. */
  private aiModelToolsOff?: () => void;
  private aiModelCardOff?: () => void;
  /** …and how a PDF report the data scientist laid out looks in the transcript. */
  private aiReportCardOff?: () => void;
  /** …and the replay sandbox's door, which needs the backend for history as well as the roster. */
  private aiModelSimulateOff?: () => void;
  /** …and the symbol tools, which are the ones that touch a PLC. */
  private aiSymbolToolsOff?: () => void;
  private aiDashboardToolsOff?: () => void;
  private aiProgramToolsOff?: () => void;
  private aiHistoryToolsOff?: () => void;
  private aiSandboxToolsOff?: () => void;
  private aiIntegrationToolsOff?: () => void;
  private aiNavigateOff?: () => void;
  /** Kept so the listener comes off with the element — see the `vitesse-ai-route` block below. */
  private onAiRoute?: (e: Event) => void;
  /** The org's authored widgets, by namespaced id — empty until `loadOrgWidgets` lands, which is
   *  the same thing "this org has authored none" looks like, and correctly so. */
  private orgWidgets = new Map<string, CatalogEntry>();
  /** The same widgets as rows, for the marketplace's community cards: who published each, and
   *  whether it is Vitesse's. */
  @state() private orgWidgetRows: OrgWidgetRow[] = [];
  /** The tile being published, while its dialog is open. */
  @state() private publishTile?: TilePublish;
  @state() private publishBusy = false;
  @state() private publishError = '';

  /**
   * Read the org's widgets into the catalogue the AI places from.
   *
   * Best-effort, like every other boot read here: a plane that cannot be reached costs the org's
   * own widgets, not the dashboard tools. `defaults` is parsed rather than trusted — it crossed a
   * wire as a JSON string, and a malformed one must cost that widget its defaults rather than
   * the whole catalogue.
   */
  private async loadOrgWidgets(): Promise<void> {
    // `ai` is set in the same boot this is called from; guarded because the field is optional.
    const rows = (await this.ai?.api.listOrgWidgets().catch(() => [])) ?? [];
    const next = new Map<string, CatalogEntry>();
    for (const w of rows) {
      let defaults: Record<string, unknown> = {};
      try {
        const parsed: unknown = JSON.parse(w.defaultsJson || '{}');
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) defaults = parsed as Record<string, unknown>;
      } catch {
        /* a widget with unreadable defaults still renders; its settings just start empty */
      }
      next.set(w.widget, {
        id: w.widget,
        title: w.title,
        summary: w.summary,
        configKeys: w.configKeys,
        defaults,
        minW: w.minW,
        minH: w.minH,
        source: w.source,
      });
    }
    this.orgWidgets = next;
    this.orgWidgetRows = rows;
  }

  /**
   * Is this source on a shelf already? The built-ins, the official marketplace and every widget
   * published to this org's shelf. Identity IS the source: a tile whose bytes match none of them
   * is a widget nobody has published, and it gets the publish button.
   */
  private knownSource = (source: string): boolean =>
    !!catalogForSource(source) || !!officialForSource(source) || this.orgWidgetRows.some((w) => w.source === source);

  /** Publish a tile's widget as the person asked: named, for the org or for everyone. */
  private async publishWidget({ tile, meta }: TilePublish, req: PublishRequest): Promise<void> {
    if (!this.ai) {
      this.publishError = t('marketplace.installNoBoards');
      return;
    }
    this.publishBusy = true;
    this.publishError = '';
    // The id is the source's own fingerprint, so an edited widget is a new one and an identical
    // one is the same one wherever it was published from.
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(tile.source));
    const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
    // The widget describes itself: its settings are its config keys, its minimum size in pixels
    // becomes grid units (a column is ~100px, a row ~72px). A widget that reported nothing falls
    // back to what its tile shows.
    const keys = meta?.settings?.length ? meta.settings.map((s) => s.key) : this.tileSettingsKeys(tile);
    const minW = meta?.minWidth ? Math.max(1, Math.ceil(meta.minWidth / 100)) : Math.max(1, Math.min(tile.layout.w, 4));
    const minH = meta?.minHeight ? Math.max(1, Math.ceil(meta.minHeight / 72)) : Math.max(1, Math.min(tile.layout.h, 3));
    const r = await this.ai.api.publishWidget({
      id: `w-${hex}`,
      title: req.title,
      summary: '',
      source: tile.source,
      configKeys: keys,
      minW,
      minH,
      visibility: req.visibility,
    });
    this.publishBusy = false;
    if (!r.ok) {
      this.publishError = r.error;
      return;
    }
    this.publishTile = undefined;
    // The shelf has a new item: the button goes, the card appears, the assistant's list grows.
    await this.loadOrgWidgets();
  }

  /** The keys a widget declares as settings, so the shelf item says what to bind. */
  private tileSettingsKeys(tile: DashboardTile): string[] {
    const keys = Object.keys(tile.config ?? {});
    return keys.filter((k) => typeof (tile.config as Record<string, unknown>)[k] !== 'object' || k === 'items' || k === 'symbols');
  }
  /** …and the org's own gateway roster — the tool nothing else could stand in for. */
  private aiGatewayToolsOff?: () => void;
  private aiMentionsOff?: () => void;
  private aiStarted = false;
  // The bell-icon notification store (no IO; fed by the FleetManager via onFleetChange).
  private readonly notifications = new NotificationManager();
  // Per-gateway managers, created lazily once + cached (keyed by deviceFingerprint).
  // `entries` is derived from the roster + this map; the map also resolves the
  // CURRENTLY-SELECTED gateway's managers for the detail views.
  private readonly managers = new Map<string, GatewayEntry>();
  // The currently-SELECTED gateway (which one a controller/model/gateway view is
  // under) — set from nav events, never hardcoded. '' on home (all gateways shown
  // at once; nothing "selected"). DISPLAY/routing only; data scope is per-entry.
  @state() private gatewayId = '';
  /** The org's LIVE fleet-roster stream port (subscribeFleet) — closed on teardown,
   *  re-opened on claim/unclaim. Not reactive: it's a transport handle, not view state. */
  private fleetPort?: MessagePort;
  /** Bumped on every (re)open and on teardown. A call that awaited past a newer one
   *  closes the port it got instead of installing it — otherwise the slower of two
   *  overlapping opens wins and the faster's port is orphaned with a live onmessage. */
  private fleetGen = 0;

  constructor() {
    super();
    new LocaleController(this); // re-render the shell (AI panel labels) on language change
  }

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    // Deep link: the URL is the initial route. Applied as plain state (no
    // animation — it's the first paint) and canonicalized via replaceState so
    // a malformed path doesn't linger in the address bar. The detail views
    // tolerate the roster arriving later (the worker creates gateway conns on
    // demand), so a deep-loaded controller page paints data immediately;
    // resolveFleet's ownership guard corrects an unowned/bogus gateway to home.
    // The options after `?` are an arrival (router `parseControllerOpen`), and the
    // canonical path written back drops them with everything else.
    const r = parseRoute(location.pathname);
    this.applyRoute(r, parseControllerOpen(location.search));
    history.replaceState(null, '', routePath(this.effectiveRoute(r)));
    window.addEventListener('popstate', this.onPopState);
    // The gated-UI overlays bubble their two intents to the shell — ONE place
    // routes them, so every gated surface behaves identically. Request-access
    // reaches the org's admins via the cloud control-plane; upgrade opens the
    // org-dialog Billing page (base-ui's openOrgBilling, on the Clerk singleton
    // the identity structurally IS).
    this.addEventListener('vitesse-gate-request-access', this.onGateRequestAccess as EventListener);
    this.addEventListener('vitesse-gate-upgrade', this.onGateUpgrade);
  }

  willUpdate(changed: PropertyValues): void {
    if (changed.has('backend') && this.backend && !this.fleet) {
      // Fleet FIRST — the first-run experience for an org that owns no gateway is
      // "discover + claim a box on your network" (relayed through the cloud). It
      // POLLS DiscoverNearby; onFleetChange feeds the claimable list/notifications.
      const fleet = new FleetManager(this.backend);
      this.fleet = fleet;
      fleet.addEventListener('change', this.onFleetChange);
      fleet.startDiscovery();
      // The app-global media plane (one per session) — shared across every camera
      // view so two views of one camera share a single PC/stream. Keyed by
      // (gateway, camera), so it's not per-gateway.
      this.cameraStreams = new CameraStreamManager(this.backend);
      // Resolve the org's CLAIMED fleet (runtime, never hardcoded). Every claimed
      // gateway becomes an entry (card + tree node) — even disconnected. A fresh
      // org that's claimed nothing gets an empty roster → the first-run claim flow.
      void this.resolveFleet();
    }
    /*
     * WHOSE THREADS THESE ARE — written on every update, not once at construction.
     *
     * The manager is built the moment the `ai` handle arrives, and `identity` frequently has not
     * resolved by then; set there, the account was the empty string and stayed it, which turns the
     * transcript cache off permanently while looking exactly like a cache that is on. It also has
     * to survive a re-auth and an account switch, and neither of those rebuilds the manager.
     *
     * The setter is idempotent and only acts on a CHANGE, so this costs nothing on the updates where
     * it has not moved.
     */
    if (this.aiManager) this.aiManager.account = identityFrom(this.identity).userId;
    // AI plane (app-global, per-user): create the manager cheaply when the handle arrives…
    if (changed.has('ai') && this.ai && !this.findings) this.findings = new FindingsManager(this.ai.api);
    if (changed.has('ai') && this.ai && !this.aiManager) {
      // The wire is passed as well as held in the AI worker: a chat turn STREAMS from the main
      // thread (the edge adapter reads the response as it arrives), which a Comlink round-trip per
      // token cannot do. Same session the rest of the shell authenticates with.
      // The one plane-wiring call (driver + live channel). It was missing here entirely, which
      // left this surface deaf to config changes: a tool gated in the console kept running
      // ungated in this app until the rail was reopened. One line, and the live sync every
      // surface is supposed to have is structural rather than ported.
      configureAiPlane(AI_URL, () => this.identity?.session?.getToken() ?? Promise.resolve(null), 'app');
      // WHAT THE USER IS LOOKING AT, which this surface could not answer at all. The tools are pure
      // DOM and identical in every app, so wiring them is one call — and without it the assistant on
      // the surface where the actual work happens has no idea what is on screen, while the console
      // does. A tool nobody implements is never offered, so this failed silently rather than loudly.
      this.aiToolsOff = implementDomPlatformTools();
      /*
       * WHICH GATEWAY — org-wide, so registered here once rather than per-relay like the tools
       * below. Reads the same reconciled `entries` the fleet stream already keeps for the home
       * page's own gateway grid: no extra round trip, and it can never show the assistant a box
       * the person can't see.
       */
      this.aiGatewayToolsOff = registerGatewayTools({
        entries: () => this.entries,
        /*
         * …AND WHAT IS ON EACH ONE, which is the half this surface can answer and the fleet
         * dashboard cannot. Measured in the local tool trail: two thirds of every failed symbol
         * search was a controller named against a gateway that does not have it, because the roster
         * listed boxes and nothing listed what was inside them. Same reconciled managers the grid
         * renders from — no round trip, and it can never name a controller the person cannot see.
         */
        controllersOf: (e) => (e as GatewayEntry).controllers.all.map((c) => ({ id: c.id, name: c.name })),
      });
      /*
       * …and this surface's OWN capabilities. A model lives on the fabric behind ITS gateway's
       * api-gateway, which the AI worker has no route to and should not grow one — so the browser
       * that can already read it is what declares that the assistant may ask.
       *
       * RESOLVED PER CALL AGAINST THE WHOLE ROSTER, not captured to "whatever is selected". Every
       * one of these tools takes an explicit `gateway` argument (see gateway-resolve.ts) and falls
       * back to `selected` only when the caller names none — so the assistant reaches any gateway
       * the org owns without depending on, or changing, what a person has open on screen.
       */
      const scope = { entries: () => this.entries, selected: () => this.selected };
      // The org's tool catalog too: models.write finds the Slack server's send_dm in it, and the
      // armings tell it whether a model's Slack messages can go out.
      this.aiModelToolsOff = registerModelTools({
        ...scope,
        mcpTools: () => this.ai?.api.listTools() ?? Promise.resolve([]),
        armings: () => this.ai?.api.listArmings() ?? Promise.resolve([]),
      });
      // And how a statechart being written LOOKS while it is written — the transcript's card body
      // for `models.write`. Registered beside the tool because the two are the same feature: the
      // app declares that the assistant may author a model, and what that looks like on screen.
      this.aiModelCardOff = registerModelWorkCard(scope);
      // And the replay: the same draft, run against recorded history in the browser's sandbox.
      // Needs the backend for the history reads; the engine and the viewer load on first use.
      this.aiModelSimulateOff = registerModelSimulateTool({ ...scope, backend: () => this.backend });
      /*
       * Dashboards are the same case as models, for the same reason: the board
       * lives behind its relay's api-gateway, which is LAN-only on most boxes
       * and unreachable from the AI worker. Reading a board is a server tool so
       * a headless driver can answer questions about one; WRITING is here, so
       * composing works on a gateway with no route to the cloud. That asymmetry
       * is deliberate — see dashboard-tools.ts.
       */
      /*
       * …AND THE WIDGETS THE ORG ITSELF WROTE, which is the dependency that has been declared since
       * the dashboard tools were written and never once supplied. `installedWidgets()` returned
       * undefined on every call, so `dashboards.widgets` could only ever offer the ten built-ins
       * and `buildTiles` could only ever answer "not installed here" — a placement path that was
       * built, asserted, and unreachable.
       *
       * Read ONCE, at boot, into a map the tools read synchronously: a tool that awaited a fetch on
       * every call would put a round trip behind listing the catalogue. A widget authored during
       * this session is picked up on the next load, which is the same freshness the built-in
       * catalogue has and is honest about — the store is the org's, not this tab's.
       */
      this.aiDashboardToolsOff = registerDashboardTools({
        ...scope,
        installedWidgets: () => this.orgWidgets,
        refreshInstalledWidgets: () => this.loadOrgWidgets(),
        // Running a widget to check it needs the sandbox frame, which is this app's (widget-check.ts).
        checkWidget: (source, config) => checkWidgetSource(source, config),
        // What a finished agent in the caller's tree returned, found by the label its spawner gave
        // it (or its run id). The deliverable when it parses; else the first fenced block of the
        // summary, for an author that wrote its widget into prose — the tile still gets a source.
        agentDeliverable: async (name, callerRunId) => {
          const want = name.trim().toLowerCase();
          const runs = callerRunId ? await treeRunsOf(callerRunId).catch(() => []) : [];
          const run = runs.find((r) => r.status === RunStatus.FINISHED && (r.runId === name || (r.task?.label ?? '').trim().toLowerCase() === want));
          if (!run) return undefined;
          const raw = run.ret?.deliverableJson?.trim() ?? '';
          try {
            const parsed: unknown = raw ? JSON.parse(raw) : undefined;
            if (parsed && typeof parsed === 'object' && typeof (parsed as { source?: unknown }).source === 'string') return parsed;
          } catch {
            // Not JSON: fall through to the summary.
          }
          const fence = /```[a-z]*\n([\s\S]*?)```/.exec(run.ret?.result?.summary ?? '');
          return fence ? { source: fence[1] } : undefined;
        },
        // The bytes of a generated image come through the AI plane, which holds the token. Read
        // live off the manager rather than captured: this registration runs before it exists.
        readConvFile: (conversationId, sha256) => this.aiManager?.readArtifact(conversationId, sha256) ?? Promise.resolve(null),
      });
      void this.loadOrgWidgets();
      /*
       * …AND WHAT A PERSON CAN POINT AT WITH @ — the same rosters, offered in the composer.
       *
       * A mention hands the AI the exact entity, so it does not have to list or search for it.
       * Registered here, beside the tools, because they read the same state: what the tools can
       * reach and what the person can mention are one set.
       */
      this.aiMentionsOff = registerPlantMentions({
        ...scope,
        widgets: () => allWidgets(this.orgWidgets),
        navigate: (route) => this.navigate(route),
      });
      /*
       * …and the symbols themselves — the tools that touch the machine.
       *
       * Same reason they are client tools: a symbol lives behind its relay's api-gateway. Same
       * security model as the rest: a write is authorized against THIS person's session, exactly as
       * if they had typed the value into the grid, so the assistant cannot reach past its operator.
       *
       * A write-locked symbol is refused by the plugin that owns it, and there is deliberately NO
       * unlock tool: unlocking is a person's decision about a machine, and an assistant that could
       * clear its own obstacle is not gated by one.
       */
      this.aiSymbolToolsOff = registerSymbolTools({ ...scope, backend: () => this.backend });
      /*
       * …and the program — read-only, list-then-read, the assistant seeing what the Program tab
       * shows. Declared here like every other client tool and STRIPPED at the AI worker's edge for
       * an org whose plan lacks functional_analysis; the browser is where reachability lives, the
       * edge is where the plan does.
       */
      this.aiProgramToolsOff = registerProgramTools({ ...scope, backend: () => this.backend });
      // Registered beside the program tools deliberately: the assistant answering
      // "why did it stop" needs the structure AND what the values did, and a
      // static answer with no history behind it is the half that sounds right.
      this.aiHistoryToolsOff = registerHistoryTools({ ...scope, backend: () => this.backend });
      /*
       * …and the OTHER half of the plant: the customer's own systems.
       *
       * The dashboard tools have accepted `integrationId` as a tile scope for a while, so the
       * assistant could build a view onto an integration it had no way of reading itself — it could
       * name a path it could not check. These close that: list what is connected, browse what is in
       * it, read a bounded page of it. The unbounded read is a `sandbox.run` dataset, which is the
       * same division history already has.
       */
      this.aiIntegrationToolsOff = registerIntegrationTools({ ...scope, backend: () => this.backend });
      /*
       * …and RUNNING CODE over what those return.
       *
       * Registered beside history for the same reason history was registered
       * beside the program tools: the tools above each cap what they return at
       * what a model can read — two hundred points, a page of rows — which is
       * right for them and leaves every question that needs the whole window
       * unanswerable. This one moves the answer instead of the data: the rows
       * are fetched here, handed to a Python interpreter that has no network,
       * and what comes back is a number or a chart rather than a spreadsheet.
       */
      this.aiSandboxToolsOff = registerSandboxTools({
        ...scope,
        backend: () => this.backend,
        // The ORG's toggle, read live off the session rather than captured: this registration runs
        // before the AI manager exists, let alone before it has handshaken, so anything captured
        // here would be `false` for the life of the tab.
        codeExecutionEnabled: () => this.aiManager?.current.codeExecutionEnabled === true,
        // A `file` dataset reads the conversation its run belongs to: the file parts its archive
        // holds, oldest first, and their bytes through the read the transcript's chips make.
        conversationFiles: (conversationId) => ({
          list: async () => {
            const files: ReturnType<typeof filesOf> = [];
            let cursor = '';
            // Bounded: 40 pages of 50 rows is a conversation far longer than any that holds a file.
            for (let page = 0; page < 40; page += 1) {
              const h = await this.ai?.api.history(conversationId, cursor);
              if (!h) break;
              for (const m of h.messages) files.push(...filesOf(m.content));
              cursor = h.nextCursor;
              if (!cursor) break;
            }
            return files;
          },
          read: (sha256) => this.aiManager?.readArtifact(conversationId, sha256) ?? Promise.resolve(null),
        }),
      });
      // The data scientist's PDF, shown in its card: the bytes the turn's download chip reads. The
      // card is handed no conversation, so it reads through the open one — the worker serves a file
      // the same person stored in any of their conversations, which covers a specialist's thread.
      this.aiReportCardOff = registerReportCard({
        read: (sha256) => this.aiManager?.readArtifact(this.aiManager.openConversation?.id ?? '', sha256) ?? Promise.resolve(null),
      });
      /*
       * …and MOVING the person, which every roster already offered and no app but the console
       * implemented.
       *
       * `platform.navigate` is deliberately not one of the DOM defaults — every app routes
       * differently and a tool that guessed would send someone to a page that does not exist — so
       * it sat in three specialists' tool lists as a name nothing answered. Supplying it here is
       * what turns "the racecar model is on this page" into actually being on that page.
       *
       * VALIDATED BY ROUND-TRIP THROUGH THE APP'S OWN GRAMMAR, not against a second list of routes.
       * `parseRoute` is deliberately tolerant — anything it does not recognise becomes home — so a
       * naive implementation would take a mistyped path, land on the home page, and report success.
       * Re-formatting the parse and requiring it to match is exactly the check that catches that,
       * and it cannot drift from the router because it IS the router.
       *
       * The move itself is the same pushState the shell's own `navigate` does, through the same
       * popstate listener, so the assistant moving the user is indistinguishable from the user
       * moving themselves: one history entry, and Back does what they expect.
       */
      /*
       * THE SAME MOVE, ASKED FOR BY A TOOL RATHER THAN BY THE MODEL.
       *
       * `models.write` knows the gateway and the new model id at the moment it creates one; making
       * the assistant call `platform.navigate` afterwards costs a whole extra model call to say
       * something already known, and it forgets. So a client tool may REQUEST a route, and this
       * honours it through exactly the same router — the request carries a path and nothing else,
       * and an unparseable one is ignored rather than landing anyone on the home page.
       */
      this.onAiRoute = (e: Event) => {
        const path = String((e as CustomEvent<{ path?: string }>).detail?.path ?? '');
        if (!path.startsWith('/')) return;
        const route = parseRoute(path);
        if (routePath(route) !== path.replace(/\/$/, '')) return;
        this.navigate(route);
      };
      window.addEventListener('vitesse-ai-route', this.onAiRoute);

      this.aiNavigateOff = implementPlatformTool('platform.navigate', (args) => {
        const raw = String(args.path ?? '').trim();
        const path = raw.startsWith('/') ? raw.split(/[?#]/)[0] : `/${raw.split(/[?#]/)[0]}`;
        // The options after `?` used to be cut off here with the fragment, so "open its History tab"
        // landed on Symbols and the tool still said it had taken them there. The path is validated
        // exactly as before; the options only shape the page it names.
        const open = parseControllerOpen(raw.match(/\?[^#]*/)?.[0] ?? '');
        const route = parseRoute(path);
        const canonical = routePath(route);
        if (canonical !== (path === '/' ? '/' : path.replace(/\/$/, ''))) {
          return {
            ok: false,
            message:
              `There is no page at "${raw}". This app has / (home), /flows, /findings, /gateways/<id>, and under a relay: ` +
              '/controllers/<id>, /cameras/<id>, /models/<id>, /integrations/<id>, /dashboards/<id>. ' +
              'A controller page takes ?tab=history&symbol=<path>&symbol=<path>&minutes=<n> to open its History on those symbols. ' +
              'Use gateways.list or models.list for the ids.',
          };
        }
        // A real page shape can still name a relay or controller that is not there — see `unknownPlace`.
        const missing = unknownPlace({ entries: () => this.entries, selected: () => this.selected }, route);
        if (missing) return { ok: false, message: `${missing} Nothing was opened.` };
        const shown = route.view === 'controller' ? open : undefined;
        this.navigate(route, true, shown);
        // Say what is on screen, not what was asked for: the model goes on to talk about it.
        const what = !shown
          ? ''
          : shown.tab !== 'history'
            ? `, on its ${shown.tab} tab`
            : `, on its History tab` +
              (shown.symbols.length ? ` showing ${shown.symbols.join(', ')}` : '') +
              (shown.minutes ? ` over the last ${shown.minutes} minutes` : '');
        const ignored = open && !shown ? ' The options after "?" apply only to a controller page, so they were ignored.' : '';
        return { ok: true, message: `Took the user to ${canonical}${what}. They are looking at it now.${ignored}` };
      });
      // 'app', not 'vitesse'. The platform string is the key config rows are stored under
      // ('app' | 'fleet' | 'ai'), and a surface announcing a name nothing else uses cannot be
      // configured: no row can ever match it, so it silently took empty defaults — no base prompt,
      // no org prompt, no experts, web search off.
      this.aiManager = new AiManager(this.ai, 'app', {
        aiUrl: AI_URL,
        token: () => this.identity?.session?.getToken() ?? Promise.resolve(null),
      });
      /*
       * HOW THIS APP RUNS WHAT AN AGENT MADE.
       *
       * The panel's viewer column shows an artifact's source and its declaration on its own; running
       * it needs the widget frame, and the frame is ours. `vitesse-widget-preview` (the dashboard grid with one tile, fed the demo readings) is the right host
       * for it: an opaque-origin frame with a deny-all grant and synthetic values, so a widget can
       * be looked at before anything of the plant is handed to it.
       *
       * NO SERIES, deliberately. What a reviewer needs to see first is whether the thing survives
       * frame one with nothing to read — which is exactly how a widget written against the wrong SDK
       * fails — and a widget that draws a clean empty state here is one that will not break a board.
       */
      this.aiManager.artifactPreview = (a) =>
        html`<vitesse-widget-preview .source=${a.source} .config=${a.config} heading=${a.title}></vitesse-widget-preview>`;
      /*
       * AND ITS SOURCE READS LIKE EVERY OTHER PIECE OF CODE IN THIS APP.
       *
       * The same viewer a PLC program and a statechart get: Monaco, line numbers, highlighting,
       * folding, hover. It is ours to lend because Monaco is ours — it lands in its own lazy chunk
       * on first mount, so a reader who never opens the tab never pays for it, and the surfaces
       * that have no editor (the fleet view, the console) fall back to the tokenised text.
       */
      this.aiManager.artifactCode = (d) =>
        html`<vitesse-code-view
          class="ai-code"
          .value=${d.text}
          language=${
            d.lang === 'json'
              ? 'json'
              : d.lang === 'ts'
                ? 'typescript'
                : d.lang === 'js'
                  ? 'javascript'
                  : d.lang === 'html'
                    ? 'html'
                    : d.lang === 'xml'
                      ? 'xml'
                      : 'none'
          }
          ?editable=${Boolean(d.editable)}
          .onEdit=${d.onEdit}
          .onProblems=${d.onProblems}
        ></vitesse-code-view>`;
    }
    /*
     * WHOSE THREADS THESE ARE — written on every update, not once at construction.
     *
     * The manager is built the moment the `ai` handle arrives and `identity` frequently has not
     * resolved by then, so set inside that branch the account was the empty string and stayed it —
     * which turns the transcript cache off permanently while looking exactly like a cache that is
     * on. It also has to survive a re-auth and an account switch, and neither of those rebuilds the
     * manager.
     *
     * AFTER the construction, so the update that builds the manager also names it; the setter only
     * acts on a CHANGE, so this costs nothing on the updates where it has not moved.
     */
    if (this.aiManager) this.aiManager.account = identityFrom(this.identity).userId;
    /*
     * …and handshake (mint the ephemeral token) on the first rail-open — idle-until-used.
     *
     * THE CONDITION IS "SHOULD BE STARTED AND IS NOT", never "the flag just changed". It used to
     * require `changed.has('aiOpen')`, and the manager is built one branch above on
     * `changed.has('ai')` — two different updates, in an order nothing guarantees. So:
     *
     *   1. the page loads; `ai` has not arrived, so `aiManager` is null;
     *   2. the person opens the rail; `aiOpen` flips, `changed` carries it, and the guard fails on
     *      the null manager;
     *   3. `ai` arrives; the manager is built; `changed` carries `ai`, not `aiOpen`.
     *
     * The edge is gone, and gone for the life of the page: `aiOpen` never changes again because it
     * is already true. The rail is open, the assistant answers, and it has ONLY the browser's own
     * tools — no web search, no sub-agents, no agent notes, no MCP; twenty server tools missing,
     * with nothing on screen to say so. Asked to search, it correctly reports it cannot, which
     * reads as a permission problem and is not one.
     *
     * How likely depends entirely on how fast the token resolves, which is why it survived: on a
     * warm load `ai` wins the race and everything works.
     *
     * `aiStarted` already makes this once-only, so the `changed` test was never what protected it —
     * it was only what could lose the edge. fleet-ui had the right shape: it starts from the rail's
     * own open event, which has no ordering to lose.
     */
    if (this.aiOpen && this.aiManager && !this.aiStarted) {
      this.aiStarted = true;
      void this.aiManager.start();
    }
  }

  /**
   * Resolve the org's claimed gateways (via the cloud control-plane) into
   * `entries` — one per gateway the ACTIVE org owns, each with its own live
   * managers (created once, cached). The org filter is load-bearing: ListFleet
   * returns the WHOLE fleet to a super-admin, but only boxes the active org owns
   * are this org's. Re-run on claim/unclaim (a new box appears / a gone one is
   * dropped). Never throws — a transient cloud blip leaves the prior roster for
   * the next poll/claim to re-resolve.
   */
  private async resolveFleet(): Promise<void> {
    if (!this.backend) return;
    // BOOT-FROM-CACHE: paint the last-known roster instantly (first resolve only);
    // the live stream's snapshot replaces + reconciles it ~½s later. managersFor()
    // starts each gateway's streams, whose sources emit their own cached frame-zeros
    // — so the whole tree boots from cache.
    if (this.entries.length === 0) {
      try {
        const myOrg = identityFrom(this.identity).orgId;
        const cached = await this.backend.api.listFleetCached();
        const ownedCached = myOrg && cached ? cached.filter((g) => g.orgId === myOrg) : [];
        if (ownedCached.length > 0) this.reconcileRoster(ownedCached);
      } catch {
        /* cache miss — the live stream fills in */
      }
    }
    // LIVE: (re)open the fleet stream. Its first frame is the AUTHORITATIVE snapshot
    // (membership truth); ~1 Hz deltas then keep presence/health live with no reload.
    // Re-called on claim/unclaim to force a fresh snapshot — the partial-delta wire
    // carries no removal, so re-subscribing is how a gone box leaves the roster.
    void this.openFleetStream();
  }

  /** Open (or re-open) the org's LIVE fleet stream — the streaming replacement for
   *  the one-shot listFleet. Each `snapshot` frame is the full current roster, fed
   *  to the idempotent reconciler. supervise() in the worker re-opens on a cloud
   *  drop; we re-open here on claim/unclaim to re-snapshot. */
  private async openFleetStream(): Promise<void> {
    if (!this.backend) return;
    /*
     * ASK THE SAME ANNOTATION THE SERVER ENFORCES.
     *
     * SubscribeFleet used to be annotated `public`, with the scoping done inside
     * the handler — a member whose gateway:view had been revoked got a
     * scoped-to-nothing roster and no refusal. It now declares the permission, so
     * a revoked member is refused, and opening a stream that is certain to be
     * refused would churn the supervisor's reconnect loop against a denial that
     * will never clear.
     *
     * Not a <vitesse-gated> wrapper because there is no control here to lock —
     * this is a data subscription, and the honest lock is not opening it.
     */
    if (getSession()?.allowsRpc(FleetControl.method.subscribeFleet) === false) return;
    const gen = ++this.fleetGen;
    this.fleetPort?.close(); // drop the prior subscription → fresh authoritative snapshot
    this.fleetPort = undefined;
    let port: MessagePort;
    try {
      port = await this.backend.api.subscribeFleet();
    } catch (err) {
      // Keep the prior roster; the next poll/claim re-resolves (resolveFleet's contract).
      console.error('[fleet] subscribeFleet failed', err);
      return;
    }
    if (gen !== this.fleetGen) {
      port.close(); // superseded (re-opened or torn down) while awaiting
      return;
    }
    this.fleetPort = port;
    port.onmessage = (e: MessageEvent) => {
      const frame = e.data as { kind?: string; items?: FleetGateway[]; cloudServers?: CloudServerLite[] };
      // 'snapshot' carries the full roster (the worker folds deltas into it); 'error'
      // frames keep the last roster while supervise re-opens.
      if (frame.kind === 'snapshot' && frame.items) {
        this.rawFleet = frame.items; // unfiltered — the server already applied access
        this.cloudServers = frame.cloudServers ?? [];
        this.reconcileRoster(frame.items);
      }
    };
  }

  /** Reconcile the org's owned gateways into `entries`. Membership change (claim/
   *  unclaim) → full rebuild (create/evict managers). Steady state (the common ~1 Hz
   *  presence/health delta) → refresh each entry's live gateway record IN PLACE, no
   *  manager churn — so a box flipping offline updates the card without re-creating
   *  its streams. Cheap at the stream rate AND scalable (no per-tick RPC fan-out). */
  private reconcileRoster(gws: FleetGateway[]): void {
    const myOrg = identityFrom(this.identity).orgId;
    const owned = myOrg ? gws.filter((g) => g.orgId === myOrg) : [];
    const ownedIds = new Set(owned.map((g) => g.deviceFingerprint));
    const membershipChanged = ownedIds.size !== this.entries.length || this.entries.some((e) => !ownedIds.has(e.gateway.deviceFingerprint));
    if (membershipChanged) {
      this.evictDeparted(ownedIds);
      this.entries = owned.map((g) => this.managersFor(g)); // new array → Lit re-renders
      // Keep the selection valid: if the selected gateway vanished (unclaimed), drop
      // back to home. Never auto-select — home shows ALL gateways at once.
      if (this.gatewayId && !ownedIds.has(this.gatewayId)) {
        this.gatewayId = '';
        if (this.view !== 'home') this.view = 'home';
        history.replaceState(null, '', '/'); // the URL named a gateway this org doesn't own
      }
      return;
    }
    // Steady state: refresh the live record on each existing entry (status/lastSeen/
    // health/IPs) without touching its managers. Re-send LAN candidates only when a
    // NIC IP actually changed (the cloud A-record sweep), not every tick.
    const byId = new Map(owned.map((g) => [g.deviceFingerprint, g]));
    for (const e of this.entries) {
      const g = byId.get(e.gateway.deviceFingerprint);
      if (!g) continue;
      const nicsChanged = g.itIp !== e.gateway.itIp || g.otIp !== e.gateway.otIp || g.wifiIp !== e.gateway.wifiIp;
      e.gateway = g; // status/lastSeenMs now live → deriveConnState re-evaluates
      if (nicsChanged) {
        void this.backend?.api.setLanCandidates(
          g.deviceFingerprint,
          lanCandidates(g.deviceFingerprint, g).map((l) => l.url),
        );
      }
    }
    this.requestUpdate(); // entries identity unchanged → nudge; the structSig gate decides
  }

  /**
   * Get-or-create one gateway's entry (its 3 live managers, started once). The
   * shell subscribes to each manager's `change` → requestUpdate, so the cards +
   * tree (which read `entry.controllers.all` at render) stay live without their
   * own listeners. Idempotent — repeated resolves reuse the cached managers, so a
   * gateway's streams aren't torn down + rebuilt on every fleet poll.
   */
  private managersFor(gateway: FleetGateway): GatewayEntry {
    const id = gateway.deviceFingerprint;
    // Hand the worker this box's LAN-direct legs (it/ot/wifi.<fp>...) derived from
    // its reported NICs — the worker races them vs cloud + live-switches. Re-sent on
    // every fleet resolve so a NIC-IP change (the cloud A-record sweep) propagates;
    // empty clears them (cloud-only). Idempotent in the worker.
    void this.backend?.api.setLanCandidates(
      id,
      lanCandidates(id, gateway).map((l) => l.url),
    );
    const existing = this.managers.get(id);
    if (existing) {
      existing.gateway = gateway; // refresh roster metadata (displayName/online/…)
      return existing;
    }
    const controllers = new ControllerManager(this.backend!, id);
    const cameras = new CameraManager(this.backend!, id);
    const models = new ModelManager(this.backend!, id);
    const integrations = new IntegrationManager(this.backend!, id);
    const dashboards = new DashboardManager(this.backend!, id);
    const transport = new TransportManager(this.backend!, id);
    const pluginTypes = new PluginTypesManager(this.backend!, id);
    // The frontend connect/disconnect intent — orchestrates stop/start of the
    // data managers above + pauses the worker's race (frontend-only; the box is
    // never touched). Lives on the entry so home/detail/tree read it directly.
    const connection = new ConnectionManager(this.backend!, id, { controllers, cameras, models, integrations, dashboards, pluginTypes });
    const entry: GatewayEntry = { gateway, controllers, cameras, models, integrations, dashboards, transport, pluginTypes, connection };
    // A manager's data changed → re-render home/tree — but ONLY when something
    // they actually DISPLAY changed (structural-signature gate, coalesced to one
    // rAF). ModelManager fires `change` at the model state-transition rate (~30/s
    // for a fast cycler — `currentState`/`elapsedMs` churn), none of which the
    // tree/cards show; without the gate that reassigned `entries` ~30/s and
    // re-rendered the ALWAYS-MOUNTED nav tree + home grid fleet-wide. The gate
    // makes a pure-runtime tick a cheap signature compare with zero DOM work.
    const rerender = (): void => this.scheduleStructuralRerender();
    controllers.addEventListener('change', rerender);
    cameras.addEventListener('change', rerender);
    models.addEventListener('change', rerender);
    integrations.addEventListener('change', rerender);
    dashboards.addEventListener('change', rerender);
    transport.addEventListener('change', rerender);
    pluginTypes.addEventListener('change', rerender);
    connection.addEventListener('change', rerender);
    this.managers.set(id, entry);
    void controllers.start();
    void cameras.start(); // DIAGNOSTIC: re-enabled with loud [CAMERA DIAG] error logging
    void models.start();
    void integrations.start();
    void dashboards.start();
    void transport.start();
    void pluginTypes.start();
    return entry;
  }

  /**
   * Evict gateways that left the org (unclaimed / re-claimed elsewhere / dropped
   * from the roster). `managersFor` only ever ADDS to `this.managers`; without
   * this, a departed gateway keeps its 4 live Connect streams (= NATS interest on
   * the api-gateway) + 5 `change` listeners + a 3s worker probe timer open for the
   * life of the session — the headline B2B-scale leak under fleet churn. Stopping
   * the managers closes their streams; `disposeGateway` frees the worker conn +
   * its probe timer; deleting the entry drops the managers (GC'd with listeners).
   */
  private evictDeparted(ownedIds: Set<string>): void {
    for (const [id, e] of this.managers) {
      if (ownedIds.has(id)) continue;
      e.controllers.stop();
      e.cameras.stop();
      e.models.stop();
      e.integrations.stop();
      e.dashboards.stop();
      e.transport.stop();
      e.pluginTypes.stop();
      this.managers.delete(id);
      void this.backend?.api.disposeGateway(id);
    }
  }

  // ── Structural-rerender gate (see the `rerender` comment in managersFor) ──
  private structRerenderScheduled = false;
  private lastStructSig = '';

  /** Coalesce manager `change` bursts into ONE rAF, then re-render the tree/home
   *  only if the structural signature (what they DISPLAY) actually moved. A
   *  pure-runtime model tick (~30/s) hits the early-return → no `entries` churn,
   *  no DOM. O(entities) signature build per frame, but ZERO DOM re-render when
   *  nothing shown changed — vs the old full re-render of the always-mounted tree
   *  + home grid on every tick. */
  private scheduleStructuralRerender(): void {
    if (this.structRerenderScheduled) return;
    this.structRerenderScheduled = true;
    requestAnimationFrame(() => {
      this.structRerenderScheduled = false;
      const sig = this.structSig();
      if (sig === this.lastStructSig) return; // only runtime churned — nothing displayed changed
      this.lastStructSig = sig;
      this.entries = [...this.entries];
    });
  }

  /** A string over EVERYTHING the tree + home cards render, and NOTHING they
   *  don't. EXCLUDES the model's per-transition runtime (`currentState`,
   *  `elapsedMs`, `startedAtMs`) — the ~30/s churn the tree (name only) and the
   *  cards (name + description + status) never show. Gateway metadata
   *  (displayName/NICs) reaches `entries` via resolveFleet's direct reassign, not
   *  this gate, so it isn't needed here; transport (path+RTT) + conn-state + the
   *  controller/model descriptor fields ARE, since those arrive via manager
   *  `change`. */
  private structSig(): string {
    const p: string[] = [];
    for (const e of this.entries) {
      const ti = e.transport.transportInfo;
      p.push('G', e.gateway.deviceFingerprint, entryConnState(e), ti.path, String(ti.rttMs));
      for (const c of e.controllers.all) {
        p.push('C', c.id, c.name, c.status, c.discoveryStatus ?? '', String(c.symbolCount ?? 0));
      }
      for (const m of e.models.all) {
        p.push('M', m.id, m.name, m.status, m.desiredState, m.error, m.description, String(m.outputCount), String(m.wiring?.inputs.length ?? 0));
      }
    }
    return p.join('\x1f');
  }

  /** The currently-selected gateway's entry (managers), for the detail views. */
  private get selected(): GatewayEntry | undefined {
    return this.gatewayId ? this.managers.get(this.gatewayId) : undefined;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.aiNavigateOff?.();
    if (this.onAiRoute) window.removeEventListener('vitesse-ai-route', this.onAiRoute);
    window.removeEventListener('popstate', this.onPopState);
    this.removeEventListener('vitesse-gate-request-access', this.onGateRequestAccess as EventListener);
    this.removeEventListener('vitesse-gate-upgrade', this.onGateUpgrade);
    this.aiToolsOff?.();
    this.aiModelToolsOff?.();
    this.aiModelCardOff?.();
    this.aiReportCardOff?.();
    this.aiModelSimulateOff?.();
    this.aiSymbolToolsOff?.();
    this.aiDashboardToolsOff?.();
    this.aiProgramToolsOff?.();
    this.aiHistoryToolsOff?.();
    this.aiSandboxToolsOff?.();
    this.aiIntegrationToolsOff?.();
    this.aiGatewayToolsOff?.();
    this.aiMentionsOff?.();
    this.fleetGen++; // an open still in flight must not install its port after teardown
    this.fleetPort?.close(); // stop the live fleet stream (the worker aborts its subscription)
    this.fleet?.removeEventListener('change', this.onFleetChange);
    this.fleet?.stopDiscovery();
    for (const e of this.managers.values()) {
      e.controllers.stop();
      e.cameras.stop();
      e.models.stop();
      e.integrations.stop();
      e.dashboards.stop();
      e.transport.stop();
      e.pluginTypes.stop();
    }
  }

  /**
   * Reconcile bell notifications + the home claim popup off the FleetManager's
   * nearby-unclaimed set. CLAIM-GATED: only users who can actually claim (admins,
   * mirrored from authz.can — the server re-enforces) are nagged; for everyone
   * else any stale claimable notification is cleared. One `claim:<fp>` notification
   * per nearby box (idempotent); boxes that left the LAN (or got claimed) are
   * removed. On HOME, a newly-seen claimable box auto-opens the popup once.
   */
  private readonly onFleetChange = (): void => {
    const nearby = this.fleet?.nearby ?? [];
    // Session, not plain `can` — see vitesse-claim-dialog. A revoked or
    // surface-scoped grant must clear these notifications, not surface them.
    if (!(getSession()?.can(Perm.GatewayClaim) ?? false)) {
      for (const n of this.notifications.list) if (n.kind === 'claimable') this.notifications.remove(n.id);
      return;
    }
    const present = new Set(nearby.map((g) => `claim:${g.deviceFingerprint}`));
    for (const g of nearby) {
      this.notifications.upsert({
        id: `claim:${g.deviceFingerprint}`,
        kind: 'claimable',
        title: t('notifications.claimableTitle'),
        body: '', // no gateway id in the bell — just the title; the row routes home to claim
        data: g,
      });
    }
    for (const n of this.notifications.list) {
      if (n.kind === 'claimable' && !present.has(n.id)) this.notifications.remove(n.id);
    }
    // No auto-opened modal: discovery surfaces as the home banner + bell
    // notification, both dismissible. (The modal is still reachable from the
    // header's claim action for an explicit re-open.)
  };

  /**
   * The ONE navigation entry point — every view change (tree/card nav events,
   * the header home button, popstate, deep links) converges here, so the URL
   * and the view state can never disagree. `push` distinguishes user-initiated
   * navigation (writes a history entry) from history-initiated (popstate — the
   * entry already exists) and boot (replaceState canonicalizes).
   */
  private navigate(d: NavDetail, push = true, open?: ControllerOpen): void {
    if (push) {
      const path = routePath(this.effectiveRoute(d));
      if (path !== location.pathname) history.pushState(null, '', path);
    }
    const dir = slideDirection(this.currentNode(), this.nodeForDetail(d));
    this.animateTo(dir, () => this.applyRoute(d, open));
  }

  /** Apply a route to the view state (no URL/history side effects). */
  /*
   * KIOSK MODE — the wall-display rotation. Started from a gateway page's
   * Kiosk button, carried here (the shell owns navigation, and the rotation IS
   * navigation), rendered by the dashboard page, and ended by leaving the
   * dashboard view — walking to the screen and clicking anything is exiting,
   * which is the only exit a person at a wall expects to need.
   */
  @state() private kioskMode = false;

  private applyRoute(d: NavDetail, open?: ControllerOpen): void {
    if (d.view !== 'dashboard') this.kioskMode = false;
    // Every move replaces the arrival, and a move without one clears it — otherwise leaving the
    // controller and coming back would re-open whatever the last link asked for.
    this.controllerOpen = open && d.view === 'controller' ? { ...open, seq: ++this.openSeq } : undefined;
    this.view = d.view;
    // The selected gateway comes from the nav event (each card/tree node knows
    // its own gateway) — that's how a controller/model resolves to the right
    // entry's managers. 'home' is org-wide (all gateways) → no selection.
    if (d.view === 'home') this.gatewayId = '';
    else if (d.gatewayId) this.gatewayId = d.gatewayId;
    if (d.controllerId) this.selectedControllerId = d.controllerId;
    if (d.cameraId) this.selectedCameraId = d.cameraId;
    if (d.modelId) this.selectedModelId = d.modelId;
    if (d.integrationId) this.selectedIntegrationId = d.integrationId;
    if (d.dashboardId) this.selectedDashboardId = d.dashboardId;
  }

  /**
   * The route `d` lands on once applied — nav events may omit ids (a tree node
   * emits only what it knows; selections are sticky), so the URL is formatted
   * from the MERGED result, mirroring applyRoute's semantics exactly.
   */
  private effectiveRoute(d: NavDetail): Route {
    return {
      view: d.view,
      gatewayId: d.view === 'home' || d.view === 'canvas' || d.view === 'findings' ? '' : d.gatewayId || this.gatewayId,
      controllerId: d.controllerId ?? this.selectedControllerId,
      cameraId: d.cameraId ?? this.selectedCameraId,
      modelId: d.modelId ?? this.selectedModelId,
      integrationId: d.integrationId ?? this.selectedIntegrationId,
      dashboardId: d.dashboardId ?? this.selectedDashboardId,
    };
  }

  /** A gated surface's Request-access button was pressed — carry the ask to the
   *  org's admins. The overlay already flipped to "sent" optimistically, so only
   *  a real failure needs surfacing. */
  private readonly onGateRequestAccess = (e: CustomEvent<{ permission: string; context: string }>): void => {
    void this.backend?.api
      .requestAccess(e.detail.permission, e.detail.context)
      .catch((err: unknown) => showResultError(err, 'Request access'));
  };

  /** A gated surface's Upgrade button — open the org-dialog Billing page. The
   *  identity IS the Clerk singleton (it satisfies ClerkSwitcherHost
   *  structurally); openOrgBilling no-ops safely if the modal API is absent. */
  private readonly onGateUpgrade = (): void => {
    if (this.identity) openOrgBilling(this.identity as unknown as Parameters<typeof openOrgBilling>[0]);
  };

  private readonly onPopState = (): void => {
    // A link in a chat answer arrives here (pushState + popstate, as the navigate tool), options
    // and all. Applied once, then the bare path goes back in the address bar — see `parseControllerOpen`.
    const open = parseControllerOpen(location.search);
    this.navigate(parseRoute(location.pathname), false, open);
    if (open) history.replaceState(null, '', location.pathname);
  };

  /** Map the live view state to its tree node (the navigation SOURCE). */
  private currentNode(): NavNode {
    return this.nodeForDetail({ view: this.view, gatewayId: this.gatewayId, controllerId: this.selectedControllerId });
  }

  /**
   * Map a route to its position in the app tree. home(0) → gateway(1) →
   * controller(2); sibling controllers ordered by their list index so a
   * controller→controller hop slides vertically in a consistent direction.
   * Symbol/program/model sub-pages slot in at depth 3+ with no change to the
   * direction logic (nav-tree.ts is route-agnostic).
   */
  private nodeForDetail(d: NavDetail): NavNode {
    const gwKey = `gateway:${this.gatewayId}`;
    switch (d.view) {
      case 'canvas':
        return { key: 'canvas', depth: 1, parentKey: 'home', order: -1 };
      case 'findings':
        return { key: 'findings', depth: 1, parentKey: 'home', order: -0.5 }; // below Flows, above the relays
      case 'gateway':
        return { key: gwKey, depth: 1, parentKey: 'home', order: 0 };
      case 'controller': {
        const cid = d.controllerId ?? this.selectedControllerId;
        const idx = this.selected?.controllers.all.findIndex((c) => c.id === cid) ?? -1;
        return { key: `controller:${cid}`, depth: 2, parentKey: gwKey, order: idx < 0 ? 0 : idx };
      }
      case 'camera': {
        const camId = d.cameraId ?? this.selectedCameraId;
        const idx = this.selected?.cameras.all.findIndex((c) => c.id === camId) ?? -1;
        return { key: `camera:${camId}`, depth: 2, parentKey: gwKey, order: idx < 0 ? 0 : idx };
      }
      case 'model': {
        const mid = d.modelId ?? this.selectedModelId;
        const idx = this.selected?.models.all.findIndex((m) => m.id === mid) ?? -1;
        return { key: `model:${mid}`, depth: 2, parentKey: gwKey, order: idx < 0 ? 0 : idx };
      }
      case 'integration': {
        const iid = d.integrationId ?? this.selectedIntegrationId;
        const idx = this.selected?.integrations.all.findIndex((x) => x.id === iid) ?? -1;
        return { key: `integration:${iid}`, depth: 2, parentKey: gwKey, order: idx < 0 ? 0 : idx };
      }
      case 'dashboard': {
        const did = d.dashboardId ?? this.selectedDashboardId;
        const idx = this.selected?.dashboards.all.findIndex((x) => x.id === did) ?? -1;
        return { key: `dashboard:${did}`, depth: 2, parentKey: gwKey, order: idx < 0 ? 0 : idx };
      }
      default:
        return { key: 'home', depth: 0, parentKey: null, order: 0 };
    }
  }

  /**
   * Run a view change inside a DIRECTIONAL View Transition. The browser snapshots
   * the before/after DOM; `data-vt-dir` on <html> selects which slide the
   * ::view-transition pseudos play (see the directional CSS in render()), so the
   * page slides the way the tree move implies — RIGHT into a child, LEFT back to a
   * parent, VERTICAL between siblings. Native + GPU-driven + zero-dependency
   * (Baseline since Oct 2025); the header + tree snapshot to themselves and don't
   * flicker. The ~240ms slide also masks the page's data-fetch RTT. 'none' (same
   * page / first paint) or no VT support → instant apply.
   */
  private animateTo(dir: SlideDir, apply: () => void): void {
    const root = document.documentElement;
    const start = (
      document as Document & {
        startViewTransition?: (cb: () => unknown) => { finished?: Promise<unknown>; ready?: Promise<unknown> };
      }
    ).startViewTransition;
    if (typeof start !== 'function' || dir === 'none') {
      apply();
      return;
    }
    root.dataset.vtDir = dir;
    const clear = (): void => {
      if (root.dataset.vtDir === dir) delete root.dataset.vtDir; // don't clobber a newer nav's dir
    };
    const t = start.call(document, () => {
      apply();
      return this.updateComplete;
    });
    // Both `.ready` and `.finished` REJECT with InvalidStateError when the browser
    // SKIPS the transition — aborted by a newer nav mid-slide, or an invalid state
    // on the first paint. We handle `.finished` (via clear), but `.ready` was
    // unguarded, so that benign abort surfaced as an unhandled-rejection on every
    // boot. Swallow it; `clear` still releases data-vt-dir.
    t?.ready?.catch(() => {});
    if (t?.finished) t.finished.then(clear, clear);
    else clear();
  }

  // Composite keys that match each tree node's unique `id` (which encodes the
  // OWNING gateway). A bare entity id collides across gateways — the same model
  // (same name + scxml) on two gateways shares a model id, so a bare-id match
  // would light up BOTH. A model runs in exactly one gateway instance, so the
  // selection key is per-(gateway, entity), never per-entity.
  private get treeSelected(): string {
    if (this.view === 'controller') return `controller-${this.gatewayId}-${this.selectedControllerId}`;
    if (this.view === 'camera') return `camera-${this.gatewayId}-${this.selectedCameraId}`;
    if (this.view === 'model') return `model-${this.gatewayId}-${this.selectedModelId}`;
    if (this.view === 'integration') return `integration-${this.gatewayId}-${this.selectedIntegrationId}`;
    if (this.view === 'dashboard') return `dashboard-${this.gatewayId}-${this.selectedDashboardId}`;
    if (this.view === 'gateway') return `gateway-${this.gatewayId}`;
    if (this.view === 'canvas') return 'canvas';
    if (this.view === 'findings') return 'findings';
    return '';
  }

  /**
   * The catalogue widgets worth a marketplace card, previewed for real: the same widget host a
   * board uses, with no gateway behind it, so each draws its face and reads "--". A card's
   * download hands over the vanilla source + config; the picker then asks which board.
   */
  /** A preview may read nothing and never expires: DENY_ALL's zero expiry would fault it at once. */
  private static readonly PREVIEW_GRANT: CapabilityGrant = { ...DENY_ALL, expiresAtMs: null };

  private marketplaceListings(): MarketplaceListing[] {
    // What people published — this org's own, and every org's global ones — under their names.
    // Only Vitesse's carry the badge; a publisher's name is never the test.
    const community: MarketplaceListing[] = this.orgWidgetRows.map((w) => ({
      id: w.widget,
      kind: 'widgets' as const,
      official: w.official,
      author: w.official ? undefined : w.publishedByOrgName || w.publishedByOrg,
      title: w.title,
      summary: w.summary,
      widget: { source: w.source, config: {} },
      preview: () => html`<div class="marketplace-preview marketplace-preview-widget">
        <vitesse-widget-host .source=${w.source} .config=${{}} .previewValues=${[]} .grant=${VitesseApp.PREVIEW_GRANT}></vitesse-widget-host>
      </div>`,
    }));
    // One source with the assistant's toolbox: what is on this shelf is what it can place.
    return [...officialWidgets().map((w) => ({
      id: w.id,
      kind: 'widgets' as const,
      official: true,
      title: w.title,
      summary: w.summary,
      widget: { source: w.entry.source, config: w.config },
      preview: () => html`<div class="marketplace-preview marketplace-preview-widget">
        ${w.preview === 'demo'
          ? html`<vitesse-widget-demo .source=${w.entry.source} .config=${w.previewConfig} .series=${w.series} .rate=${w.rate}></vitesse-widget-demo>`
          : html`<vitesse-widget-host .source=${w.entry.source} .config=${w.id === 'note' ? { content: NOTE_DEMO } : w.previewConfig} .previewValues=${[]} .grant=${VitesseApp.PREVIEW_GRANT}></vitesse-widget-host>`}
      </div>`,
    })), ...community];
  }

  private toggleMarketplace = (): void => {
    this.marketOpen = !this.marketOpen;
    // Same pane rule as the AI rail: opening a right-side panel collapses the
    // tree; closing leaves the tree wherever the user last put it.
    if (this.marketOpen) this.treeOpen = false;
  };

  /* Both panes move through the shared rule — see rail-resize.onToggleAi. */
  private toggleAi = (): void => {
    const next = onToggleAi({ treeOpen: this.treeOpen, aiOpen: this.aiOpen });
    this.treeOpen = next.treeOpen;
    this.aiOpen = next.aiOpen;
    if (this.aiOpen) this.aiUnseen = false; // looked at it
  };
  private toggleTree = (): void => {
    this.treeOpen = onToggleTree({ treeOpen: this.treeOpen, aiOpen: this.aiOpen }).treeOpen;
  };

  /** What the findings log's ids are called here — relays, controllers, models, people. */
  private findingNames() {
    return findingNames(this.entries, identityFrom(this.identity).userId);
  }

  private renderMain() {
    // The selected gateway's managers feed the detail views. (canvas/flows is
    // org-wide — it gets the selected entry for now; true cross-gateway flows is
    // a later pass, per the roadmap.)
    const e = this.selected;
    switch (this.view) {
      case 'canvas':
        // The Flows page is a telemetry:flows surface — the floor plan sees its
        // relays; watching them flow is the paid step. Ultra's extra pill-bar
        // sections don't exist yet ("coming soon"), so there's nothing above
        // flows to gate here today.
        return html`<vitesse-gate-overlay
          capability="telemetry"
          level="flows"
          context="flows"
          .content=${html`<vitesse-canvas .entries=${this.entries} .backend=${this.backend}></vitesse-canvas>`}
        ></vitesse-gate-overlay>`;
      case 'findings':
        // Gated on the RPC it reads: a member without ai:finding:view is told why, not shown an
        // empty log. Passed as `.content`, as Flows is, so a denied reader never mounts the list —
        // slotted under <vitesse-gated> it still loaded itself behind the lock, four refused reads
        // per visit (see vitesse-gate-overlay).
        return html`<vitesse-gate-overlay
          .rpc=${AiAdmin.method.listFindings}
          context="findings"
          .content=${html`<vitesse-findings .manager=${this.findings} .names=${this.findingNames()}></vitesse-findings>`}
        ></vitesse-gate-overlay>`;
      case 'gateway':
        return html`<vitesse-gateway-detail
          .gatewayId=${this.gatewayId}
          .gatewayName=${e ? gatewayLabel(e.gateway) : this.gatewayId}
          .network=${e?.gateway}
          .gateway=${e?.gateway}
          .connection=${e?.connection}
          .controllerManager=${e?.controllers}
          .cameraManager=${e?.cameras}
          .cameraStreams=${this.cameraStreams}
          .modelManager=${e?.models}
          .integrationManager=${e?.integrations}
          .transport=${e?.transport}
          .pluginTypesManager=${e?.pluginTypes}
          .dashboardManager=${e?.dashboards}
          .backend=${this.backend}
                  @kiosk-start=${(e: CustomEvent<{ gatewayId: string; dashboardId: string }>) => {
            // The rotation is navigation, so the shell owns the flag; the
            // dashboard page runs the timer and this stays one boolean.
            this.kioskMode = true;
            this.navigate({ view: 'dashboard', gatewayId: e.detail.gatewayId, dashboardId: e.detail.dashboardId });
          }}
        ></vitesse-gateway-detail>`;
      case 'controller': {
        const c = e?.controllers.all.find((x) => x.id === this.selectedControllerId);
        return html`<vitesse-controller-detail
          .backend=${this.backend}
          .gatewayId=${this.gatewayId}
          .gatewayName=${e ? gatewayLabel(e.gateway) : this.gatewayId}
          .controllerId=${this.selectedControllerId}
          .controller=${c}
          .pluginTypesManager=${e?.pluginTypes}
          .findings=${this.findings}
          .findingNames=${this.findingNames()}
          .open=${this.controllerOpen}
        ></vitesse-controller-detail>`;
      }
      case 'camera':
        return html`<vitesse-camera-detail
          .gatewayId=${this.gatewayId}
          .gatewayName=${e ? gatewayLabel(e.gateway) : this.gatewayId}
          .cameraManager=${e?.cameras}
          .cameraStreams=${this.cameraStreams}
          .cameraId=${this.selectedCameraId}
        ></vitesse-camera-detail>`;
      case 'model':
        return html`<vitesse-model-detail
          .backend=${this.backend}
          .gatewayId=${this.gatewayId}
          .gatewayName=${e ? gatewayLabel(e.gateway) : this.gatewayId}
          .models=${e?.models}
          .controllers=${e?.controllers}
          .integrations=${e?.integrations}
          .gateways=${this.entries}
          .modelId=${this.selectedModelId}
          .aiApi=${this.ai?.api}
          .userId=${identityFrom(this.identity).userId}
          .canAdmin=${identityFrom(this.identity).role === 'admin'}
          .experts=${(this.aiManager?.current.experts ?? []).map((e) => ({ id: e.id, description: `${e.model} · effort ${e.effort}` }))}
        ></vitesse-model-detail>`;
      case 'dashboard':
        return html`<vitesse-dashboard-detail
          .backend=${this.backend}
          .gatewayId=${this.gatewayId}
          .gatewayName=${e ? gatewayLabel(e.gateway) : this.gatewayId}
          .dashboards=${e?.dashboards}
          .controllers=${e?.controllers}
          .models=${e?.models}
          .integrations=${e?.integrations}
          .dashboardId=${this.selectedDashboardId}
          .knownSource=${this.knownSource}
          @tile-publish=${(ev: CustomEvent<TilePublish>) => { this.publishTile = ev.detail; this.publishError = ''; }}
          .connState=${e ? entryConnState(e) : 'disconnected'}
          .grant=${this.dashboardGrant()}
          .selfId=${identityFrom(this.identity).userId}
          .selfName=${[this.identity?.user?.firstName, (this.identity?.user as { lastName?: string | null } | null | undefined)?.lastName].filter(Boolean).join(' ')}
          .kiosk=${this.kioskMode}
          @kiosk-stop=${() => (this.kioskMode = false)}
          .mapFleet=${this.rawFleet}
          .mapServers=${this.cloudServers}
          .orgNames=${orgNamesFrom(this.identity)}
        ></vitesse-dashboard-detail>`;
      case 'integration':
        return html`<vitesse-integration-detail
          .backend=${this.backend}
          .gatewayId=${this.gatewayId}
          .gatewayName=${e ? gatewayLabel(e.gateway) : this.gatewayId}
          .integrations=${e?.integrations}
          .integrationId=${this.selectedIntegrationId}
        ></vitesse-integration-detail>`;
      default:
        return html`<vitesse-home
          .identity=${this.identity}
          .gateways=${this.entries}
          .fleet=${this.fleet}
          .backend=${this.backend}
          .cameraStreams=${this.cameraStreams}
          @gateway-claimed=${() => void this.resolveFleet()}
        ></vitesse-home>`;
    }
  }

  /**
   * Cached per gateway. A grant built inline in render() is a new object every
   * paint, and a widget host reads its grant as a mount-time input - so a fresh
   * one restarts every tile on this page on every render, which with live
   * values means several times a second and a dashboard that never finishes
   * starting.
   */
  private grantCache?: { gatewayId: string; grant: ReturnType<typeof sessionGrant> };

  private dashboardGrant(): ReturnType<typeof sessionGrant> {
    if (this.grantCache?.gatewayId !== this.gatewayId) {
      this.grantCache = { gatewayId: this.gatewayId, grant: sessionGrant(this.gatewayId) };
    }
    return this.grantCache.grant;
  }

  render() {
    // Kiosk is a wall display: the board gets the whole screen. The header, the tree and both
    // rails go, without touching treeOpen/aiOpen/marketOpen, so leaving kiosk puts back the
    // shell the person had. The way out is the dashboard's own compact bar (Back, Exit kiosk).
    const kiosk = this.kioskMode;
    // No gateway owned → the normal shell still renders (home = the welcome/
    // landing with an empty grid). Discovery surfaces as a dismissible banner +
    // bell notification, never a forced screen. The data managers only start
    // once a gateway is owned (resolveGateway/claim), so the welcome has no data.
    return html`
      <!-- DIRECTIONAL page transitions (View Transitions API). Only .main-content
           carries a view-transition-name, so the header + tree don't flicker —
           just the page body slides. The slide DIRECTION is chosen by the tree
           move (animateTo sets data-vt-dir on <html>): RIGHT into a child, LEFT
           back to a parent, VERTICAL between siblings — so navigation reads as
           movement through one spatial tree. Document-level pseudos, so this
           <style> lives in light DOM (vitesse-app's render root). -->
      <style>
        @keyframes vt-out-toleft {
          to {
            transform: translateX(-40px);
            opacity: 0;
          }
        }
        @keyframes vt-in-fromright {
          from {
            transform: translateX(40px);
            opacity: 0;
          }
          to {
            transform: none;
            opacity: 1;
          }
        }
        @keyframes vt-out-toright {
          to {
            transform: translateX(40px);
            opacity: 0;
          }
        }
        @keyframes vt-in-fromleft {
          from {
            transform: translateX(-40px);
            opacity: 0;
          }
          to {
            transform: none;
            opacity: 1;
          }
        }
        @keyframes vt-out-toup {
          to {
            transform: translateY(-28px);
            opacity: 0;
          }
        }
        @keyframes vt-in-frombelow {
          from {
            transform: translateY(28px);
            opacity: 0;
          }
          to {
            transform: none;
            opacity: 1;
          }
        }
        @keyframes vt-out-todown {
          to {
            transform: translateY(28px);
            opacity: 0;
          }
        }
        @keyframes vt-in-fromabove {
          from {
            transform: translateY(-28px);
            opacity: 0;
          }
          to {
            transform: none;
            opacity: 1;
          }
        }
        @keyframes vt-fade-out {
          to {
            opacity: 0;
          }
        }
        @keyframes vt-fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        /* Descend toward a child → push in from the right. */
        [data-vt-dir='right']::view-transition-old(main-view) {
          animation: vt-out-toleft 0.2s ease both;
        }
        [data-vt-dir='right']::view-transition-new(main-view) {
          animation: vt-in-fromright 0.24s ease both;
        }
        /* Ascend toward a parent → pop back to the left. */
        [data-vt-dir='left']::view-transition-old(main-view) {
          animation: vt-out-toright 0.2s ease both;
        }
        [data-vt-dir='left']::view-transition-new(main-view) {
          animation: vt-in-fromleft 0.24s ease both;
        }
        /* Sibling (later) → slide up. */
        [data-vt-dir='up']::view-transition-old(main-view) {
          animation: vt-out-toup 0.2s ease both;
        }
        [data-vt-dir='up']::view-transition-new(main-view) {
          animation: vt-in-frombelow 0.24s ease both;
        }
        /* Sibling (earlier) → slide down. */
        [data-vt-dir='down']::view-transition-old(main-view) {
          animation: vt-out-todown 0.2s ease both;
        }
        [data-vt-dir='down']::view-transition-new(main-view) {
          animation: vt-in-fromabove 0.24s ease both;
        }
        /* Fallback (a VT with no direction set) — a gentle fade. */
        ::view-transition-old(main-view) {
          animation: vt-fade-out 0.16s ease both;
        }
        ::view-transition-new(main-view) {
          animation: vt-fade-in 0.2s ease both;
        }

        @media (prefers-reduced-motion: reduce) {
          ::view-transition-old(main-view),
          ::view-transition-new(main-view) {
            animation:
              vt-fade-out 0.12s ease both,
              vt-fade-in 0.12s ease both;
          }
        }
      </style>
      <vitesse-header
        style=${kiosk ? 'display:none' : nothing}
        .notifications=${this.notifications}
        .aiOpen=${this.aiOpen}
        marketplace
        .marketplaceOpen=${this.marketOpen}
        @vitesse-toggle-marketplace=${this.toggleMarketplace}
        .aiBusy=${this.aiBusy}
        .aiDone=${this.aiUnseen}
        @vitesse-toggle-ai=${this.toggleAi}
        @vitesse-toggle-tree=${this.toggleTree}
        @vitesse-home=${() => this.navigate({ view: 'home' })}
        @vitesse-claim-open=${() => (this.claimOpen = true)}
      >
        ${this.orgEl} ${this.userEl}
      </vitesse-header>
      <div
        class="main-layout"
        style="height:${kiosk ? '100vh' : 'calc(100vh - 64px)'};display:flex"
        @navigate=${(e: CustomEvent<NavDetail>) => this.navigate(e.detail)}
        @vitesse-marketplace-install=${(e: CustomEvent<InstallWidget>) => (this.installWidget = e.detail)}
        @vitesse-ai-prompt=${() => (this.aiOpen = true)}
        @vitesse-ai-activity=${(e: CustomEvent<{ busy: boolean; id?: string }>) => {
          // Folded into the tally, because several surfaces report at once — see AiBusyTally.
          this.busyTally.note(e.detail.id ?? 'anon', e.detail.busy);
          const busy = this.busyTally.working;
          // Finishing UNSEEN is the interesting transition: it is the moment the closed rail becomes
          // worth reopening. Finishing while it is open is just the answer arriving.
          if (this.aiBusy && !busy && !this.aiOpen) this.aiUnseen = true;
          this.aiBusy = busy;
        }}
      >
        <!-- Left tree rail — width-animated show/hide via the header's list-tree button. -->
        <div
          style="${kiosk ? 'display:none;' : ''}width:${
            this.treeOpen ? '250px' : '0'
          };flex-shrink:0;border-right:1px solid var(--border-color);overflow:hidden;transition:width 0.22s ease"
        >
          <div style="width:250px;height:100%">
            <vitesse-tree .gateways=${this.entries} .backend=${this.backend} .selectedId=${this.treeSelected}></vitesse-tree>
          </div>
        </div>
        <div class="main-content" style="flex:1;min-width:0;overflow-y:auto;view-transition-name:main-view">${this.renderMain()}</div>
        <!-- The marketplace rail — SAME rail element as the AI's, so width, wall
             and animation cannot drift between the two. It sits before the AI rail
             in the row, which is what "opens to the AI panel's left" means in flex. -->
        <!-- The panel's own Close dispatches the same toggle the header button does; it bubbles up
             through this rail, not the header, so the rail listens too. -->
        <vitesse-ai-rail ?open=${this.marketOpen && !kiosk} max-width=${RAIL_MAX_W / 2} storage-key="vitesse.marketplace.railWidth" @vitesse-toggle-marketplace=${this.toggleMarketplace}>
          ${this.marketOpen
            ? html`<vitesse-marketplace-panel .extraListings=${this.marketplaceListings()}></vitesse-marketplace-panel>`
            : null}
        </vitesse-ai-rail>
        <vitesse-widget-publish-dialog
          .open=${!!this.publishTile}
          .name=${this.publishTile?.tile.title ?? this.publishTile?.meta?.title ?? ''}
          .busy=${this.publishBusy}
          .error=${this.publishError}
          @publish-confirm=${(ev: CustomEvent<PublishRequest>) => { if (this.publishTile) void this.publishWidget(this.publishTile, ev.detail); }}
          @publish-close=${() => (this.publishTile = undefined)}
        ></vitesse-widget-publish-dialog>
        <vitesse-widget-install-dialog
          .widget=${this.installWidget}
          .entries=${this.entries}
          .currentGateway=${this.gatewayId}
          @dialog-close=${() => (this.installWidget = undefined)}
        ></vitesse-widget-install-dialog>
        <!-- Right AI rail. The element owns its width, the open/close animation and the drag
             grip — this app contributes only what goes inside it, so the rail cannot drift from the
             one in every other Vitesse UI. -->
        <vitesse-ai-rail ?open=${this.aiOpen && !kiosk}>
          <vitesse-ai-panel .manager=${this.aiManager} userName=${this.identity?.user?.firstName ?? ''}></vitesse-ai-panel>
        </vitesse-ai-rail>
      </div>
      <!-- Quick "claim a nearby gateway" popup — opened auto on home for a newly-
           discovered claimable box, or from a bell notification. Admin-gated inside
           (server re-enforces). The ONE claim surface; fleet/admin is a separate UI. -->
      <vitesse-claim-dialog
        .open=${this.claimOpen}
        .fleetManager=${this.fleet}
        .identity=${this.identity}
        @dialog-close=${() => (this.claimOpen = false)}
        @gateway-claimed=${() => void this.resolveFleet()}
      ></vitesse-claim-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-app': VitesseApp;
  }
}
